import { z } from 'zod';
import {
  actionDescriptionValidation,
  actionDueDateValidation,
  actionOwnerValidation,
  actionTitleValidations,
} from '../../../issue/schemas/common';
import {
  GetIssueTemplateFormSchemaArgs,
  actionDueDateValidations,
  actionSequentialDueDateValidation,
  baseSchema,
  issueDueDateDisablePastValidation,
  issueDueDateEarlierThanSLAPastValidation,
  issueDueDateOnlyNumbersValidation,
} from './common';

export function getIssueTemplateSchema({
  t,
  timezoneOffset,
  disablePast,
  SLAs,
  isDateEarlierThanSLA,
}: GetIssueTemplateFormSchemaArgs) {
  return (
    baseSchema
      .extend({
        managerId: baseSchema.shape.managerId.nullable().optional(),
        ownerId: baseSchema.shape.ownerId.nullable().optional(),
        approverId: baseSchema.shape.approverId.nullable().optional(),
        severity: baseSchema.shape.severity.nullable().optional(),
        dueDate: baseSchema.shape.dueDate.optional(),
      })
      /**
       * these validations required using reference to other fields and outside data
       * validation logic is explicitly defined here to make it more readable
       * every error needs to be push individually to the context
       * the first part checks the Assign validations
       * the second part checks the Workflow validations
       */
      .superRefine((data, ctx) => {
        if (!data.title) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['title'],
            message: t('issue:form.errorMessages.title.required'),
          });
        }
        if (!data.issueTypeId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['issueTypeId'],
            message: t('issue:form.errorMessages.issueType.required'),
          });
        }
        if (!data.executionType) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['executionType'],
            message: t('issue:form.errorMessages.executionType.required'),
          });
        }

        if (data.isAutomatic) {
          if (!data.managerId) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['managerId'],
              message: t('issue:form.errorMessages.manager.required'),
            });
          }
          if (!data.ownerId) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['ownerId'],
              message: t('issue:form.errorMessages.owner.required'),
            });
          }
          if (!data.approverId) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['approverId'],
              message: t('issue:form.errorMessages.approver.required'),
            });
          }
          if (!data.severity) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['severity'],
              message: t('issue:form.errorMessages.severity.required'),
            });
          }
          if (!data.dueDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['dueDate'],
              message: t('issue:form.errorMessages.dueDate.required'),
            });
          }
        }

        issueDueDateOnlyNumbersValidation({
          dueDate: data.dueDate,
          ctx,
          t,
        });

        issueDueDateDisablePastValidation({
          timezoneOffset,
          disablePast,
          dueDate: data.dueDate,
          ctx,
          t,
        });

        issueDueDateEarlierThanSLAPastValidation({
          isDateEarlierThanSLA,
          data: {
            dueDate: data.dueDate,
            severity: data.severity,
          },
          SLAs,
          ctx,
          t,
        });

        // [title | unique title | due date past | due date before issue |
        // due date sequential] validations always happen
        const standardizedTitles = new Set<string>();
        data.actions?.forEach((action, index) => {
          actionTitleValidations({
            data: {
              action,
              actions: data.actions || [],
              index,
              standardizedTitles,
            },
            isDateEarlierThanSLA,
            SLAs,
            ctx,
            t,
          });
          actionDueDateValidations({
            data: {
              action,
              dueDate: data.dueDate,
              index,
              isAutomatic: data.isAutomatic,
            },
            timezoneOffset,
            SLAs,
            ctx,
            t,
          });
          actionSequentialDueDateValidation({
            data: {
              action,
              actions: data.actions || [],
              dueDate: action.dueDate,
              index,
              executionType: data.executionType,
            },
            ctx,
            t,
          });

          if (data.isAutomatic) {
            actionDescriptionValidation({
              data: {
                description: action.description,
                index,
              },
              ctx,
              t,
            });
            actionDueDateValidation({
              data: {
                dueDate: action.dueDate,
                index,
              },
              ctx,
              t,
            });
            actionOwnerValidation({
              data: {
                ownerId: action.ownerId,
                index,
              },
              ctx,
              t,
            });
          }
        });
      })
  );
}

export type IssueTemplateSchemaType = ReturnType<typeof getIssueTemplateSchema>;
